import { lazy, useEffect, useState } from 'react'
import { useNVModal } from '@nv/components'
import { useNVAlertsContext, useVoterUserContext } from '@nv/contexts'
import DefaultProfileImageIcon from '@nv/images/design/default-profile-image-icon'
import ThumbsUpIcon from '@nv/images/design/thumbs-up-icon'
import { NVButton } from 'libs/basic-components/src/lib/nv_button/NVButton'
import { NominatorService } from 'libs/services/src/lib/voter/NominatorService'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styles from './Candidates.module.scss'
const NVBasicModal = lazy(() => import('@nv/lazy-loadable/lib/modals/NVBasicModal'))

export const Candidates = ({ eventId, qid, event }) => {
  const { t } = useTranslation()
  const [candidates, setCandidates] = useState([])
  const navigate = useNavigate()
  const VoterUser = useVoterUserContext().voterUser
  const [currCandidateId, setCurrCandidateId] = useState(null)
  const fetchCandidatesPosition = async () => {
    const candidatesData = await NominatorService.fetchCandidates(eventId, qid)
    setCandidates(candidatesData)
  }
  const { showAlert } = useNVAlertsContext()
  const { NVModal: ConfirmEndorseModal, openModal: openConfirmEndorseModal, closeModal: closeConfirmEndorseModal } = useNVModal()
  const { NVModal: ConfirmUnendorseModal, openModal: openConfirmUnendorseModal, closeModal: closeConfirmUnendorseModal } = useNVModal()

  useEffect(() => {
    fetchCandidatesPosition()
  }, [])

  const endorseCandidate = async (candidateId) => {
    const res = await NominatorService.endorseCandidate(candidateId)
    if (res?.success) {
      showAlert({
        message: t('events.candidateEvents.candidateEndorsedSuccess'),
        icon: <ThumbsUpIcon />,
      })
    }
    fetchCandidatesPosition()
  }

  const unEndorseCandidate = async (candidateId) => {
    const res = await NominatorService.unEndorseCandidate(candidateId)
    if (res?.success) {
      showAlert({
        message: t('events.candidateEvents.candidateUnEndorsedSuccess'),
        icon: <ThumbsUpIcon style={{ transform: 'scaleY(-1)' }} />,
      })
    }
    fetchCandidatesPosition()
  }

  const onConfirmEndorse = (candidateId) => {
    setCurrCandidateId(candidateId)
    openConfirmEndorseModal()
  }

  const onConfirmUnEndorse = (candidateId) => {
    setCurrCandidateId(candidateId)
    openConfirmUnendorseModal()
  }

  return (
    <>
      <ConfirmEndorseModal>
        <NVBasicModal
          title={t('buttons.endorse')}
          icon={<ThumbsUpIcon style={{ width: '60px' }} />}
          text={t('events.candidateEvents.areYouSureEndorse')}
          showCancel={true}
          onConfirm={() => {
            endorseCandidate(currCandidateId)
            closeConfirmEndorseModal()
          }}
        />
      </ConfirmEndorseModal>

      <ConfirmUnendorseModal>
        <NVBasicModal
          title={t('buttons.unEndorse')}
          icon={<ThumbsUpIcon style={{ transform: 'scaleY(-1)', width: '60px' }} />}
          text={t('events.candidateEvents.areYouSureUnEndorse')}
          showCancel={true}
          onConfirm={() => {
            unEndorseCandidate(currCandidateId)
            closeConfirmUnendorseModal()
          }}
        />
      </ConfirmUnendorseModal>

      <div className={styles['container']}>
        <div className={styles['header']}>{t('events.labels.candidates')}</div>

        {candidates?.length === 0 ? (
          <div className={styles['no-candidates']}>{t('admin.instantVoting.thisEventHasNoCandidates')}</div>
        ) : (
          candidates.map((candidate) => (
            <div key={candidate.id} className={styles['item']}>
              <div className={styles['info']}>
                <div className={styles['user-info']}>
                  <div className={styles['image']}>
                    {candidate.profilePhotoUrl ? (
                      <img src={candidate.profilePhotoUrl} alt='candidate img' />
                    ) : (
                      <DefaultProfileImageIcon className={styles['image']} />
                    )}
                  </div>
                  {candidate.nominee.firstName} {candidate.nominee.lastName}
                  {candidate.nominee.id === VoterUser.id ? ` (${t('main.you')})` : ''}
                </div>
                <div className={styles['user-actions']}>
                  <NVButton
                    text={t('buttons.viewProfile')}
                    variant='text'
                    onClick={() => {
                      navigate(`/voting/voter/event-details/${eventId}/questions/${qid}/candidates/${candidate.id}`)
                    }}
                    classes={{ content: styles['view'] }}
                  />
                  {!event?.endorsementsRequired ? null : candidate.endorsedByUser ? (
                    <NVButton
                      icon={<ThumbsUpIcon style={{ stroke: 'var(--primary-color)', fill: 'var(--color-white)', width: '30px' }} />}
                      text={t('buttons.nominated')}
                      height={30}
                      onClick={() => onConfirmUnEndorse(candidate.id)}
                      disabled={
                        event?.nominationState !== 'started' ||
                        candidate.nominee.id === VoterUser.id ||
                        candidate.nominationState === 'admin-approved'
                      }
                    />
                  ) : (
                    <NVButton
                      icon={<ThumbsUpIcon style={{ stroke: 'var(--color-white)', width: '30px' }} />}
                      text={t('buttons.nominate')}
                      height={30}
                      classes={{ container: styles['endorse'] }}
                      variant='cancel'
                      onClick={() => onConfirmEndorse(candidate.id)}
                      disabled={event?.nominationState !== 'started' || candidate.nominee.id === VoterUser.id}
                    />
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  )
}
